import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { LogIn, KeyRound, Mail, GraduationCap, Info, Lock } from 'lucide-react';
import useNoIndex from '../hooks/useNoIndex';

export default function Login() {
  useNoIndex();
  const { login, loginWithTeacherCode, dark } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Email / Password tab
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);

  // Teacher code tab
  const [code, setCode] = useState('');
  const [pseudo, setPseudo] = useState('');
  const [loadingCode, setLoadingCode] = useState(false);

  // Bandeau contextuel si arrivée depuis une route protégée
  const fromPath = location.state?.from || null;
  const fromLabel = fromPath === '/enseignant' || fromPath?.startsWith('/enseignant')
    ? "Espace Enseignant"
    : fromPath === '/preparation-ccs'
      ? "Préparation CCS 2 Roues"
      : null;

  const goAfterLogin = async (data) => {
    // Phase 30 (refonte 4 back-offices) : redirect intelligent selon le rôle.
    //  - admin       → /admin/dashboard
    //  - secretaire  → /secretariat/dashboard
    //  - enseignant  → /moniteur/planning (Ma journée de travail)
    //  - eleve       → /eleve/dashboard   (Ma journée)
    //  - autre       → /
    // Priorité : query `?next=` > route explicite `from` (state) > is_admin (récupéré via /me) > role.
    //
    // ?next=/path permet aux CTA externes (ex: /shorts/proposer "Se connecter")
    // de ramener l'utilisateur exactement là où il était après authentification.
    const searchParams = new URLSearchParams(location.search || '');
    const nextParam = searchParams.get('next');
    if (nextParam && nextParam.startsWith('/')) {
      navigate(nextParam);
      return;
    }

    const explicitFrom = location.state?.from;
    if (explicitFrom) {
      navigate(explicitFrom);
      return;
    }

    // Récupère le vrai profil (avec is_admin) via /api/auth/me, car le login
    // renvoie seulement `role` — pas suffisant pour distinguer un moniteur promu admin.
    let profile = data;
    try {
      const me = await api.getMe();
      profile = { ...data, ...me };
    } catch (_) { /* silent — fallback sur data si /me échoue */ }

    let destination;
    if (profile.is_admin || profile.role === 'admin') {
      destination = '/admin/dashboard';
    } else if (profile.role === 'secretaire') {
      destination = '/secretariat/dashboard';
    } else if (profile.role === 'enseignant') {
      destination = '/moniteur/planning';
    } else if (profile.role === 'eleve') {
      destination = '/eleve/dashboard';
    } else {
      destination = '/';
    }
    navigate(destination);
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    setLoadingEmail(true);
    try {
      const data = await login(email, password);
      toast.success('Connexion réussie');
      await goAfterLogin(data);
    } catch (err) {
      toast.error(err.message || 'Erreur de connexion');
    } finally {
      setLoadingEmail(false);
    }
  };

  const onSubmitCode = async (e) => {
    e.preventDefault();
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode.startsWith('EDU-')) {
      toast.error("Le code doit commencer par EDU-");
      return;
    }
    setLoadingCode(true);
    try {
      const data = await loginWithTeacherCode(cleanCode, pseudo.trim());
      if (pseudo.trim()) {
        toast.success(`Bienvenue ${pseudo.trim()} !`);
      } else {
        toast.success('Accès invité enseignant activé');
      }
      await goAfterLogin(data);
    } catch (err) {
      toast.error(err.message || 'Code invalide');
    } finally {
      setLoadingCode(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-8" data-testid="login-page">
      <div className="mc-card p-6 sm:p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-5">
          <img
            src="/logo-c2r.png"
            alt="CODE 2 ROUES"
            className={`w-11 h-11 rounded-2xl object-cover bg-[#0b1b3a]`}
          />
          <div>
            <h1 className="text-xl font-black">Connexion</h1>
            <p className="text-xs text-muted-foreground">Code 2 roues · Révision Code Moto 2026</p>
          </div>
        </div>

        {/* Bandeau contextuel : pourquoi l'utilisateur est ici */}
        {fromLabel && (
          <div
            className="mb-5 rounded-xl border-2 border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary-soft))]/40 p-3.5"
            data-testid="login-protected-notice"
          >
            <div className="flex items-start gap-2.5">
              <Lock className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
              <div className="text-[13px] leading-snug text-foreground/85">
                <strong className="block text-foreground mb-0.5">
                  {fromLabel} — accès réservé
                </strong>
                <span className="text-foreground/70">
                  Connectez-vous ou créez un compte gratuit pour accéder à cette section.
                </span>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="email" data-testid="login-tab-email" className="gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email
            </TabsTrigger>
            <TabsTrigger value="code" data-testid="login-tab-code" className="gap-1.5">
              <GraduationCap className="w-3.5 h-3.5" /> Code enseignant
            </TabsTrigger>
          </TabsList>

          {/* ===== EMAIL ===== */}
          <TabsContent value="email" className="mt-4">
            <form onSubmit={onSubmitEmail} className="space-y-4">
              <div>
                <Label htmlFor="email">Adresse email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="vous@example.com"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <PasswordInput
                  id="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loadingEmail}
                className="mc-btn-primary w-full h-11"
                data-testid="login-submit-btn"
              >
                <LogIn className="w-4 h-4 mr-2" /> {loadingEmail ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>

            <div className="mt-3 text-xs text-center">
              <Link
                to="/mot-de-passe-oublie"
                className="text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
                data-testid="login-forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <div className="mt-4 text-sm text-muted-foreground text-center">
              Pas encore de compte ?{' '}
              <Link
                to="/register"
                state={fromPath ? { from: fromPath } : undefined}
                className="font-semibold text-[hsl(var(--primary))] hover:underline"
                data-testid="login-register-link"
              >
                Créer un compte
              </Link>
            </div>
          </TabsContent>

          {/* ===== TEACHER CODE ===== */}
          <TabsContent value="code" className="mt-4">
            <form onSubmit={onSubmitCode} className="space-y-4">
              <div className="rounded-xl bg-[hsl(var(--primary-soft))] border border-[hsl(var(--primary))]/20 p-3 text-xs">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
                  <div className="text-foreground/85">
                    Utilisez le code fourni par votre administration CODE 2 ROUES pour un accès
                    enseignant immédiat.
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="code" className="flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5" /> Code enseignant *
                </Label>
                <Input
                  id="code"
                  required
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  placeholder="EDU-XXXXXXXXXX"
                  style={{ fontFamily: 'monospace' }}
                  data-testid="login-code-input"
                  autoCapitalize="characters"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Format : EDU- suivi de 10 caractères</p>
              </div>

              <div>
                <Label htmlFor="pseudo">Pseudo (optionnel)</Label>
                <Input
                  id="pseudo"
                  value={pseudo}
                  onChange={e => setPseudo(e.target.value)}
                  placeholder="ex : julien_m"
                  data-testid="login-pseudo-input"
                  maxLength={30}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {pseudo.trim()
                    ? 'Votre progression sera sauvegardée sous ce pseudo'
                    : 'Sans pseudo : accès invité temporaire (progression non sauvée)'}
                </p>
              </div>

              <Button
                type="submit"
                disabled={loadingCode || !code.trim()}
                className="mc-btn-primary w-full h-11"
                data-testid="login-code-submit-btn"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                {loadingCode
                  ? 'Connexion...'
                  : (pseudo.trim() ? 'Se connecter avec mon pseudo' : 'Accéder en invité')}
              </Button>

              <div className="mt-2 text-xs text-center text-muted-foreground">
                Pas de code ? <Link to="/rejoindre-enseignant" className="font-semibold text-[hsl(var(--primary))] hover:underline" data-testid="login-request-access-link">Faire une demande</Link>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
